import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchBookForReader,
  ensurePageWindowLoaded,
  isPdfImageBook,
  needsPageWindow,
  pageDataFor,
  bookPageTotal,
} from '../utils/bookPages';
import { 
  X, ChevronLeft, ChevronRight, Star, BookOpen,
  Bookmark, BookmarkCheck,
  GraduationCap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Max height for page images: tight chrome + extra headroom; object-contain avoids stretch. */
const READER_IMAGE_MAX_HEIGHT = 'calc((100vh - 9.5rem) * 1.05)';

function vocabularyEntryToPanel(entry) {
  if (!entry) return null;
  return {
    word: entry.word,
    definition: entry.definition != null ? entry.definition : (entry.def != null ? entry.def : ''),
    emoji: entry.emoji || String.fromCodePoint(0x1f4d6),
    difficulty: entry.difficulty != null ? entry.difficulty : 'easy',
  };
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getVocabularyWordsForPage(book, availableVocabulary, currentPage, maxWords = 3) {
  if (!book || !availableVocabulary?.length) return [];

  let wordsOnCurrentPage = availableVocabulary.filter(
    (word) => word.page_number && word.page_number === currentPage
  );

  if (wordsOnCurrentPage.length === 0 && book.pages_data) {
    const currentPageData = book.pages_data.find((p) => p.page_number === currentPage);
    if (currentPageData?.text) {
      const pageText = currentPageData.text.toLowerCase();
      wordsOnCurrentPage = availableVocabulary.filter((word) => {
        const wordText = String(word.word || '').trim().toLowerCase();
        if (!wordText) return false;
        return new RegExp(`\\b${escapeRegExp(wordText)}\\b`, 'i').test(pageText);
      });
    }
  }

  if (wordsOnCurrentPage.length === 0 && book.pages_content) {
    const currentPageContent = book.pages_content[currentPage - 1];
    if (currentPageContent?.text) {
      const pageText = currentPageContent.text.toLowerCase();
      wordsOnCurrentPage = availableVocabulary.filter((word) => {
        const wordText = String(word.word || '').trim().toLowerCase();
        if (!wordText) return false;
        return new RegExp(`\\b${escapeRegExp(wordText)}\\b`, 'i').test(pageText);
      });
    }
  }

  if (wordsOnCurrentPage.length === 0) {
    const wordsPerPage = Math.ceil(availableVocabulary.length / (book.pages || 1));
    const startIndex = ((currentPage - 1) * wordsPerPage) % availableVocabulary.length;
    wordsOnCurrentPage = [availableVocabulary[startIndex]];
  }

  return wordsOnCurrentPage.slice(0, maxWords);
}

function renderVocabSymbol(emoji) {
  const raw = String(emoji ?? '').trim();
  if (!raw) {
    return <span className="text-4xl leading-none block text-center">{String.fromCodePoint(0x1f4d6)}</span>;
  }
  const digitsOnly = raw.replace(/[\s,.]/g, '');
  const isDigitSequence = /^[0-9]+$/.test(digitsOnly) && digitsOnly.length >= 2;
  if (isDigitSequence) {
    const chars = digitsOnly.split('');
    return (
      <div className="flex flex-wrap justify-center gap-1 w-full min-w-0 max-w-full">
        {chars.map((ch, i) => (
          <span
            key={`${i}-${ch}`}
            className="inline-flex items-center justify-center min-w-[1.75rem] h-8 px-1 sm:min-w-[2rem] sm:h-9 rounded-md bg-blue-600 text-white font-bold text-sm sm:text-base shadow-sm shrink-0"
          >
            {ch}
          </span>
        ))}
      </div>
    );
  }
  return (
    <span className="text-4xl leading-none block text-center break-all min-w-0 max-w-full">
      {raw}
    </span>
  );
}

const FlipBookReader = ({ bookId, onClose }) => {
  const { token, refreshUser } = useAuth();
  const bookRef = useRef(null);
  const pageRef = useRef(null);
  
  // Core state
  const [book, setBook] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  
  // Reader settings
  const [readerMode, setReaderMode] = useState('flip'); // 'flip' or 'slide'
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'spread'
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Interaction state
  const [isFlipping, setIsFlipping] = useState(false);
  const [isNextPending, setIsNextPending] = useState(false);
  const [flipDirection, setFlipDirection] = useState('right');
  const [touchStart, setTouchStart] = useState(null);
  
  // Word/vocabulary state
  const [selectedWord, setSelectedWord] = useState(null);
  const [showWordPanel, setShowWordPanel] = useState(false);
  const [vocabularyMap, setVocabularyMap] = useState({});
  const [availableVocabulary, setAvailableVocabulary] = useState([]);
  
  // Bookmarks
  const [bookmarks, setBookmarks] = useState([]);
  const [showBookmarks, setShowBookmarks] = useState(false);
  
  // Vocabulary list for image-based books
  const [showVocabList, setShowVocabList] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  // Quiz/celebration state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [quizStep, setQuizStep] = useState(0);
  const [quizResults, setQuizResults] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [saving, setSaving] = useState(false);

  // +1 Point popup animation state
  const [showPointPopup, setShowPointPopup] = useState(false);
  const [pointPopupPosition, setPointPopupPosition] = useState({ x: 0, y: 0 });

  // Audio elements for sound effects
  const pageTurnSound = useRef(null);
  const ambientMusic = useRef(null);

  // Fetch book data on mount
  useEffect(() => {
    // Reset quiz state when switching books
    setQuizData(null);
    setShowQuiz(false);
    setQuizStep(0);
    setQuizResults([]);
    setShowCelebration(false);
    
    fetchBook();
    loadBookmarks();
  }, [bookId]);

  useEffect(() => {
    const mq = window.matchMedia('(hover: none), (pointer: coarse)');
    const updatePointerMode = () => setIsTouchDevice(mq.matches);
    updatePointerMode();
    mq.addEventListener?.('change', updatePointerMode);

    return () => {
      mq.removeEventListener?.('change', updatePointerMode);
    };
  }, []);

  const fetchBook = async () => {
    try {
      const data = await fetchBookForReader(API, bookId, token);
      setBook(data);
      setCurrentPage(data.progress?.current_page || 1);

        // Keep only vocabulary words that actually exist in this book's text.
        const vocab = Array.isArray(data.vocabulary) ? data.vocabulary : [];
        const corpus = [
          data.full_text || '',
          ...(Array.isArray(data.pages_content) ? data.pages_content.map((p) => p?.text || '') : []),
          ...(Array.isArray(data.text?.pages) ? data.text.pages.map((p) => p?.text || '') : []),
        ].join(' ').toLowerCase();

        const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const filteredVocab = !corpus.trim()
          ? vocab
          : vocab.filter((entry) => {
              const word = String(entry?.word || '').trim().toLowerCase();
              if (!word) return false;
              return new RegExp(`\\b${escapeRegExp(word)}\\b`, 'i').test(corpus);
            });

        setAvailableVocabulary(filteredVocab);

        const vocabMap = {};
        filteredVocab.forEach((word) => {
          vocabMap[String(word.word || '').toLowerCase()] = word;
        });
        setVocabularyMap(vocabMap);
    } catch (err) {
      console.error('Failed to fetch book:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load missing page images when navigating large PDF books.
  useEffect(() => {
    if (loading || !book) return;
    if (!needsPageWindow(book, currentPage)) return;

    let cancelled = false;

    (async () => {
      try {
        const updated = await ensurePageWindowLoaded(
          API,
          bookId,
          token,
          book,
          currentPage
        );
        if (cancelled) return;
        setBook(updated);
      } catch (err) {
        console.error('Failed to load book pages:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [book, currentPage, loading, bookId, token]);

  const loadBookmarks = () => {
    const saved = localStorage.getItem(`bookmarks_${bookId}`);
    if (saved) {
      setBookmarks(JSON.parse(saved));
    }
  };

  const saveBookmark = () => {
    const newBookmarks = bookmarks.includes(currentPage)
      ? bookmarks.filter(p => p !== currentPage)
      : [...bookmarks, currentPage].sort((a, b) => a - b);
    
    setBookmarks(newBookmarks);
    localStorage.setItem(`bookmarks_${bookId}`, JSON.stringify(newBookmarks));
  };

  const updateProgress = async (page, completed = false) => {
    try {
      await fetch(`${API}/progress/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ book_id: bookId, page, completed })
      });
      if (completed) {
        await refreshUser();
      }
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
  };

  const pageVocabularyWords = useMemo(
    () => getVocabularyWordsForPage(book, availableVocabulary, currentPage),
    [book, availableVocabulary, currentPage]
  );

  // Page flip animation with direction
  const flipToPage = useCallback(async (targetPage, direction = 'right') => {
    if (!book || isFlipping) return;
    const newPage = Math.max(1, Math.min(targetPage, book.pages));
    if (newPage === currentPage) return;

    if (needsPageWindow(book, newPage)) {
      try {
        const updated = await ensurePageWindowLoaded(
          API,
          bookId,
          token,
          book,
          newPage
        );
        setBook(updated);
      } catch (err) {
        console.error('Failed to load book pages:', err);
        return;
      }
    }

    setFlipDirection(direction);
    setIsFlipping(true);
    
    setTimeout(() => {
      setCurrentPage(newPage);
      updateProgress(newPage);
      setSelectedWord(null);
      setShowWordPanel(false);
    }, readerMode === 'flip' ? 300 : 150);
    
    setTimeout(() => {
      setIsFlipping(false);
    }, readerMode === 'flip' ? 600 : 300);
  }, [book, currentPage, isFlipping, readerMode, bookId, token]);

  const handlePrev = () => {
    const step = viewMode === 'spread' ? 2 : 1;
    flipToPage(Math.max(1, currentPage - step), 'left');
  };
  
  const handleNext = () => {
    if (isNextPending || isFlipping) return;
    const step = viewMode === 'spread' ? 2 : 1;
    if (currentPage >= book?.pages) {
      updateProgress(currentPage, true);
      setShowCelebration(true);
    } else {
      setIsNextPending(true);
      setTimeout(() => {
        flipToPage(Math.min(book?.pages, currentPage + step), 'right');
        
        // Show +1 point popup animation
        showPointAnimation();
        
        // Play page turn sound
        playPageTurnSound();
        setIsNextPending(false);
      }, 1000);
    }
  };

  const showPointAnimation = () => {
    // Position popup near center-right where navigation happens
    setPointPopupPosition({
      x: window.innerWidth * 0.75,
      y: window.innerHeight * 0.5
    });
    setShowPointPopup(true);
    
    // Hide after animation completes
    setTimeout(() => setShowPointPopup(false), 1500);
  };

  const playPageTurnSound = () => {
    if (pageTurnSound.current) {
      pageTurnSound.current.currentTime = 0;
      pageTurnSound.current.volume = 0.3;
      pageTurnSound.current.play().catch(err => console.log('Sound play failed:', err));
    }
  };

  // Touch/swipe handlers
  const handleTouchStart = (e) => {
    if (!isTouchDevice) return;
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!isTouchDevice) return;
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    setTouchStart(null);
  };

  const handleMobileTapNavigate = (e) => {
    if (!isTouchDevice) return;
    if (!book || showQuiz || showCelebration || isFlipping) return;
    const target = e.target;
    if (target && target.closest && target.closest('button, a, input, textarea, [role="button"]')) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const tapX = e.clientX - rect.left;
    if (tapX < rect.width / 2) {
      handlePrev();
    } else {
      handleNext();
    }
  };

  // Word click handler
  const handleWordClick = (word) => {
    const cleanWord = word.toLowerCase().replace(/[^a-z]/g, '');
    const wordInfo = vocabularyMap[cleanWord];
    
    if (wordInfo) {
      setSelectedWord(vocabularyEntryToPanel(wordInfo));
      setShowWordPanel(true);
    }
  };

  const saveWord = async () => {
    if (!selectedWord || saving) return;
    setSaving(true);
    
    try {
      await fetch(`${API}/words/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          word: selectedWord.word,
          definition: selectedWord.definition,
          emoji: selectedWord.emoji,
          book_id: bookId
        })
      });
      await refreshUser();
      setShowWordPanel(false);
      setSelectedWord(null);
    } catch (err) {
      console.error('Failed to save word:', err);
    } finally {
      setSaving(false);
    }
  };

  // Quiz handlers
  const startQuiz = async () => {
    setShowCelebration(false);
    try {
      console.log(`Fetching quiz for book: ${bookId}`);
      const res = await fetch(`${API}/books/${bookId}/quiz`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.log(`Quiz fetched for ${bookId}:`, data.length, 'questions');
        console.log('First question:', data[0]?.question?.substring(0, 50));
        
        // Check if quiz data is valid and not empty
        if (!data || !Array.isArray(data) || data.length === 0) {
          alert('Quiz not available for this book yet. Please try another book!');
          setShowCelebration(true); // Show celebration again
          return;
        }
        
        // Validate quiz structure
        const firstQ = data[0];
        if (!firstQ.question || !firstQ.choices || firstQ.answer === undefined) {
          alert('Quiz data is invalid. Please contact support.');
          setShowCelebration(true);
          return;
        }
        
        setQuizData(data);
        setQuizStep(0);
        setQuizResults([]);
        setShowQuiz(true);
      }
    } catch (err) {
      console.error('Failed to fetch quiz:', err);
      alert('Failed to load quiz. Please try again later.');
    }
  };

  const handleQuizAnswer = (answerIndex) => {
    const question = quizData[quizStep];
    const isCorrect = answerIndex === question.answer;
    const newResults = [...quizResults, isCorrect];
    setQuizResults(newResults);
    
    setTimeout(async () => {
      if (quizStep < quizData.length - 1) {
        setQuizStep(quizStep + 1);
      } else {
        // Quiz complete - submit results
        const score = newResults.filter(r => r).length;
        const total = newResults.length;
        const passed = score === total; // All correct to pass
        
        try {
          await fetch(`${API}/books/${book.id}/quiz/submit`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ passed, score, total })
          });
        } catch (err) {
          console.error('Failed to submit quiz:', err);
        }
        
        setShowQuiz(false);
        onClose();
      }
    }, 1000);
  };

  // Zoom handlers
  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 2));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoomLevel(1);

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      bookRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showQuiz || showCelebration) return;
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
      if (e.key === '0') handleResetZoom();
      if (e.key === 'b') saveBookmark();
      if (e.key === 'f') toggleFullscreen();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, book, showQuiz, showCelebration, flipToPage]);

  // Render text with clickable vocabulary words
  const renderText = (text) => {
    if (!text) return null;
    
    return text.split(' ').map((word, idx) => {
      const cleanWord = word.toLowerCase().replace(/[^a-z]/g, '');
      const isClickable = vocabularyMap[cleanWord];
      
      return (
        <span key={idx}>
          <span
            onClick={() => isClickable && handleWordClick(word)}
            className={`${isClickable ? 'vocabulary-word cursor-pointer hover:bg-yellow-200 dark:hover:bg-yellow-500/30 rounded px-0.5 transition-colors underline decoration-dotted decoration-yellow-400' : ''}`}
          >
            {word}
          </span>{' '}
        </span>
      );
    });
  };

  // Render content for a specific page number
  const renderPageContent = (pageNum) => {
    const pageContent = book.pages_content?.find(p => p.page === pageNum);
    const pageData = pageDataFor(book, pageNum);
    const isImageMode = !!(pageData && pageData.image_url);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const waitingForPage =
      isPdfImageBook(book) && !isImageMode && pageNum <= bookPageTotal(book);

    return (
      <>
        {isImageMode ? (
          /* Image-based rendering for uploaded PDF books */
          <div className="flex flex-col items-center justify-center h-full w-full min-h-0">
            <img loading="lazy" 
              src={`${BACKEND_URL}${pageData.image_url}`}
              alt={`Page ${pageNum}`}
              className="w-full h-auto max-w-full mx-auto rounded-lg object-contain"
              style={{ maxHeight: READER_IMAGE_MAX_HEIGHT }}
              onError={(e) => {
                console.error('Failed to load page image:', pageData.image_url);
              }}
            />
            {/* Page Number */}
            <div className="mt-4 text-gray-400 font-medium text-sm">
              — {pageNum} —
            </div>
          </div>
        ) : waitingForPage ? (
          <div className="flex flex-col items-center justify-center h-full py-16">
            <div className="text-5xl animate-bounce mb-4">📖</div>
            <p className="text-gray-500 font-medium">Loading page {pageNum}…</p>
          </div>
        ) : (
          <>
            {/* Page Illustration */}
            <div className="text-7xl lg:text-8xl mb-4 mt-6 page-illustration">
              {pageContent?.illus || book.emoji}
            </div>

            {/* Page Text */}
            <p 
              className="text-lg lg:text-xl xl:text-2xl leading-relaxed text-gray-700 max-w-md mx-auto page-text px-4"
              style={{ fontFamily: 'Nunito' }}
            >
              {pageContent?.text ? renderText(pageContent.text) : 'Turn the page to begin reading...'}
            </p>

            {/* Page Number */}
            <div className="mt-auto mb-6 text-gray-400 font-medium text-sm page-number">
              — {pageNum} —
            </div>
          </>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]">
        <div className="text-center">
          <div className="text-7xl animate-bounce mb-4">📖</div>
          <p className="text-white text-xl font-medium">Opening your book...</p>
        </div>
      </div>
    );
  }

  if (!book) return null;

  const pageContent = book.pages_content?.find(p => p.page === currentPage);
  const pageData = pageDataFor(book, currentPage);
  const progressPct = (currentPage / book.pages) * 100;
  const isBookmarked = bookmarks.includes(currentPage);
  const isImageMode = !!(pageData && pageData.image_url);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div 
      ref={bookRef}
      className="fixed inset-0 z-[9999] flex flex-col reader-container"
      style={{ background: 'linear-gradient(135deg, var(--th-dk) 0%, var(--th-md) 35%, var(--th-lt) 65%, var(--th-lt) 100%)' }}
      data-testid="flip-book-reader"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ═══════════ TOP BAR ═══════════ */}
      <div 
        className="flex items-center justify-between px-3 lg:px-5 py-2 glass-bar shrink-0"
        style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)' }}
      >
        <div className="w-9 h-9 shrink-0" aria-hidden />
        
        <div className="text-center flex-1 mx-2 min-w-0">
          <div className="font-bold text-gray-800 text-base lg:text-lg truncate" style={{ fontFamily: 'Fredoka' }}>
            {book.emoji} {book.title}
          </div>
        </div>
        
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Word — same style as bottom dock */}
          <button
            type="button"
            onClick={() => setShowVocabList(!showVocabList)}
            data-testid="vocab-btn"
            className={`flex flex-col items-center gap-0.5 px-2 sm:px-3 py-1 rounded-xl transition-all ${
              showVocabList ? 'bg-emerald-100' : 'hover:bg-gray-100'
            }`}
            title={availableVocabulary.length ? `${availableVocabulary.length} vocabulary words` : 'Vocabulary'}
          >
            <svg viewBox="0 0 24 24" className={`w-5 h-5 sm:w-6 sm:h-6 ${showVocabList ? 'text-emerald-600' : 'text-gray-700'}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 6 C2 4 4 3 6 3 L12 4 L18 3 C20 3 22 4 22 6 L22 18 C22 20 20 21 18 21 L12 20 L6 21 C4 21 2 20 2 18Z"></path>
              <line x1="12" y1="4" x2="12" y2="20"></line>
            </svg>
            <span className={`text-[10px] sm:text-xs font-medium leading-none ${showVocabList ? 'text-emerald-600' : 'text-gray-600'}`}>Word</span>
          </button>
          {/* Spread */}
          <button
            type="button"
            onClick={() => setViewMode(viewMode === 'spread' ? 'single' : 'spread')}
            data-testid="view-mode-btn"
            className={`flex flex-col items-center gap-0.5 px-2 sm:px-3 py-1 rounded-xl transition-all ${
              viewMode === 'spread' ? 'bg-emerald-100' : 'hover:bg-gray-100'
            }`}
            title={viewMode === 'spread' ? 'Single page view' : 'Spread view'}
          >
            <svg viewBox="0 0 24 24" className={`w-5 h-5 sm:w-6 sm:h-6 ${viewMode === 'spread' ? 'text-emerald-600' : 'text-gray-700'}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="8.5" height="16" rx="2.5"></rect>
              <rect x="13.5" y="4" width="8.5" height="16" rx="2.5"></rect>
            </svg>
            <span className={`text-[10px] sm:text-xs font-medium leading-none ${viewMode === 'spread' ? 'text-emerald-600' : 'text-gray-600'}`}>Spread</span>
          </button>
          <button
            type="button"
            onClick={saveBookmark}
            data-testid="bookmark-btn"
            className={`flex flex-col items-center gap-0.5 px-2 sm:px-3 py-1 rounded-xl transition-all ${
              isBookmarked ? 'bg-yellow-100 text-yellow-600' : 'hover:bg-gray-100 hover:bg-yellow-50/80'
            }`}
            title="Bookmark this page"
          >
            {isBookmarked ? (
              <BookmarkCheck className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
            ) : (
              <Bookmark className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            )}
            <span className={`text-[10px] sm:text-xs font-medium leading-none ${isBookmarked ? 'text-yellow-700' : 'text-gray-600'}`}>
              {isBookmarked ? 'Saved' : 'Mark'}
            </span>
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="reader-close-btn"
            className="flex flex-col items-center gap-0.5 px-2 sm:px-3 py-1 rounded-xl transition-all text-gray-700 hover:bg-red-50 hover:text-red-600"
            title="Close reader"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" strokeWidth={2.25} />
            <span className="text-[10px] sm:text-xs font-medium leading-none">Close</span>
          </button>
        </div>
      </div>

      {/* ═══════════ PAGE DOT PROGRESS (upper) ═══════════ */}
      <div
        className="shrink-0 px-3 sm:px-4 py-2 border-b border-white/40"
        style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(8px)' }}
      >
        <div
          className="flex gap-1.5 justify-center overflow-x-auto max-w-full py-1 reader-page-dots"
          data-testid="page-dot-progress"
        >
          {Array.from({ length: book.pages }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => flipToPage(i + 1, i + 1 > currentPage ? 'right' : 'left')}
              disabled={isFlipping}
              aria-label={`Go to page ${i + 1}`}
              className={`h-2 rounded-full transition-all shrink-0 ${
                i + 1 === currentPage
                  ? 'w-6 bg-emerald-500'
                  : i + 1 < currentPage
                    ? 'w-2 bg-emerald-400'
                    : 'w-2 bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
        <p className="text-center text-[10px] sm:text-xs text-gray-500 mt-1.5">
          Page {currentPage} of {book.pages} · {Math.round(progressPct)}% complete
        </p>
      </div>

      {/* ═══════════ MAIN READING AREA ═══════════ */}
      <div
        className="group/mobile-nav flex-1 flex items-center justify-center px-1 sm:px-2 md:px-3 py-0 overflow-hidden relative min-h-0"
        onClick={handleMobileTapNavigate}
      >
        {/* Navigation Zones */}
        <div 
          className="absolute left-4 top-1/2 -translate-y-1/2 cursor-pointer z-10 hidden md:block"
          onClick={handlePrev}
        >
          {currentPage > 1 && (
            <div className="w-14 h-14 rounded-full bg-white border border-gray-200/90 hover:bg-gray-50 shadow-lg flex items-center justify-center nav-btn transition-all max-md:opacity-0 max-md:pointer-events-none max-md:group-hover/mobile-nav:opacity-100 max-md:group-hover/mobile-nav:pointer-events-auto">
              <ChevronLeft className="w-7 h-7 text-gray-900" strokeWidth={3} />
            </div>
          )}
        </div>
        <div 
          className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer z-10 hidden md:block"
          onClick={handleNext}
        >
          {currentPage < book.pages && (
            <div className="w-14 h-14 rounded-full bg-white border border-gray-200/90 hover:bg-gray-50 shadow-lg flex items-center justify-center nav-btn transition-all max-md:opacity-0 max-md:pointer-events-none max-md:group-hover/mobile-nav:opacity-100 max-md:group-hover/mobile-nav:pointer-events-auto">
              <ChevronRight className="w-7 h-7 text-gray-900" strokeWidth={3} />
            </div>
          )}
        </div>

        {/* Book Container - Spread or Single View */}
        {viewMode === 'spread' ? (
          /* ═══════════ SPREAD VIEW (TWO PAGES) ═══════════ */
          <div className="flex w-full max-w-full items-stretch justify-center gap-0 min-w-0">
            {/* Left Page */}
            <div
              className={`page-card bg-white rounded-l-3xl shadow-2xl text-center relative overflow-hidden flex flex-col min-h-0 ${
                isImageMode ? 'p-2 sm:p-3 md:p-4' : 'p-8'
              }`}
              style={{ 
                flex: '1 1 0',
                minWidth: 0,
                maxWidth: '50%',
                minHeight: 'min(600px, 50vh)',
                maxHeight: READER_IMAGE_MAX_HEIGHT,
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center',
                borderRight: '1px solid #e5e7eb'
              }}
            >
              {renderPageContent(currentPage)}
            </div>

            {/* Right Page */}
            {currentPage + 1 <= book.pages && (
              <div
                className={`page-card bg-white rounded-r-3xl shadow-2xl text-center relative overflow-hidden flex flex-col min-h-0 ${
                  isImageMode ? 'p-2 sm:p-3 md:p-4' : 'p-8'
                }`}
                style={{ 
                  flex: '1 1 0',
                  minWidth: 0,
                  maxWidth: '50%',
                  minHeight: 'min(600px, 50vh)',
                  maxHeight: READER_IMAGE_MAX_HEIGHT,
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: 'center center'
                }}
              >
                {renderPageContent(currentPage + 1)}
              </div>
            )}
          </div>
        ) : (
          /* ═══════════ SINGLE VIEW ═══════════ */
          <div 
            className={`book-container ${readerMode === 'flip' ? 'flip-mode' : 'slide-mode'}`}
            style={{ perspective: '2000px' }}
          >
            <div
              ref={pageRef}
              className={`
                page-wrapper bg-white rounded-3xl shadow-2xl 
                w-full max-w-[min(100vw-1rem,96rem)] mx-auto text-center relative overflow-hidden
                ${isImageMode ? 'p-3 sm:p-4 md:p-6' : 'p-6 lg:p-10'}
                ${isFlipping ? `flipping ${flipDirection}` : ''}
                ${readerMode}
              `}
              style={{ 
                minHeight: isImageMode ? 'min(420px, 55vh)' : '450px',
                maxHeight: isImageMode ? READER_IMAGE_MAX_HEIGHT : undefined,
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center',
                transition: 'transform 0.3s ease'
              }}
            >
              {/* Page shadow effect */}
              <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-gray-100/50 to-transparent pointer-events-none" />
              <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-gray-100/50 to-transparent pointer-events-none" />
              
              {/* Page corner fold */}
              <div className="page-corner" />

              {renderPageContent(currentPage)}

              {/* Reading tip for first page */}
              {currentPage === 1 && Object.keys(vocabularyMap).length > 0 && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-xs text-gray-400 flex items-center gap-1 bg-yellow-50 px-3 py-1 rounded-full">
                  <span className="text-yellow-500">💡</span>
                  <span>Tap <span className="underline decoration-dotted decoration-yellow-400">underlined words</span> to learn them!</span>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* ═══════════ BOTTOM NAVIGATION ═══════════ */}
      <div 
        className="flex items-center justify-between px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] glass-bar shrink-0"
        style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)' }}
      >
        <button
          onClick={handlePrev}
          disabled={currentPage <= 1 || isFlipping}
          data-testid="reader-prev-btn"
          className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full font-bold text-sm sm:text-base transition-all ${
            currentPage <= 1 || isFlipping
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 active:scale-95'
          }`}
        >
          <ChevronLeft className="w-5 h-5 shrink-0" />
          <span className="hidden sm:inline">Back</span>
        </button>

        {/* Center: vocabulary words for this page */}
        <div className="flex flex-col items-center min-w-0 flex-1 px-2 text-center">
          {pageVocabularyWords.length > 0 ? (
            <div
              className="flex flex-wrap justify-center gap-1.5 max-w-[min(100%,16rem)] sm:max-w-xs"
              data-testid="footer-vocabulary-words"
            >
              {pageVocabularyWords.map((word) => (
                <button
                  key={`${word.word}-${word.emoji || ''}`}
                  type="button"
                  onClick={() => {
                    setSelectedWord(vocabularyEntryToPanel(word));
                    setShowWordPanel(true);
                  }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-gray-800 text-xs sm:text-sm font-semibold hover:bg-amber-100 transition-colors max-w-full"
                  title="Tap to learn this word"
                >
                  <span className="shrink-0">{word.emoji || '📖'}</span>
                  <span className="truncate underline decoration-dotted decoration-amber-500">
                    {word.word}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <span className="text-xs sm:text-sm text-gray-400">Tap Word above to explore vocabulary</span>
          )}
        </div>

        <button
          onClick={handleNext}
          disabled={isFlipping || isNextPending}
          data-testid="reader-next-btn"
          className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-full font-bold text-sm sm:text-base transition-all active:scale-95 ${
            (isFlipping || isNextPending) ? 'opacity-50 cursor-not-allowed' : ''
          } ${
            currentPage >= book.pages 
              ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
              : 'bg-emerald-500 text-white hover:bg-emerald-600'
          }`}
        >
          <span className="hidden sm:inline">{currentPage >= book.pages ? 'Finish!' : 'Next'}</span>
          <span className="sm:hidden">{currentPage >= book.pages ? '🎉' : '→'}</span>
          <ChevronRight className="w-5 h-5 shrink-0" />
        </button>
      </div>

      {/* ═══════════ VOCABULARY LIST PANEL (for image-based books) ═══════════ */}
      {showVocabList && availableVocabulary.length > 0 && (
        <div className="absolute top-20 right-4 z-50 bg-white rounded-2xl shadow-2xl p-4 w-80 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-emerald-500" />
              Vocabulary ({availableVocabulary.length} words)
            </h3>
            <button
              onClick={() => setShowVocabList(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-3">
            {availableVocabulary.map((word, idx) => (
              <div 
                key={idx}
                className="p-3 bg-gray-50 rounded-xl hover:bg-emerald-50 transition-colors cursor-pointer min-w-0"
                onClick={() => {
                  setSelectedWord(vocabularyEntryToPanel(word));
                  setShowWordPanel(true);
                  setShowVocabList(false);
                }}
              >
                <div className="flex items-center gap-2 mb-1 min-w-0">
                  <span className="text-2xl shrink-0">{word.emoji}</span>
                  <span className="font-bold text-gray-800 capitalize min-w-0 break-words">{word.word}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                    word.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                    word.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {word.difficulty}
                  </span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2 min-w-0 break-words">{word.definition ?? word.def}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════ WORD PANEL ═══════════ */}
      {showWordPanel && selectedWord && (
        <div className="absolute bottom-32 left-4 right-4 z-50 mx-auto min-w-0 max-w-[calc(100vw-2rem)] lg:left-1/2 lg:right-auto lg:w-96 lg:max-w-[min(24rem,calc(100vw-2rem))] lg:-translate-x-1/2">
          <div 
            className="relative bg-white rounded-2xl shadow-2xl p-5 pt-6 word-panel-animate w-full max-w-full min-w-0 box-border overflow-hidden"
            style={{ border: '3px solid #FCD34D' }}
          >
            <button
              type="button"
              onClick={() => setShowWordPanel(false)}
              className="absolute top-3 right-3 z-10 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close word panel"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex min-w-0 w-full flex-col gap-3 pr-7">
              <div className="min-w-0 w-full">{renderVocabSymbol(selectedWord.emoji)}</div>
              <div className="min-w-0 w-full space-y-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="min-w-0 break-words text-xl font-bold capitalize text-gray-800" style={{ fontFamily: 'Fredoka' }}>
                    {selectedWord.word}
                  </span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
                    selectedWord.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                    selectedWord.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {selectedWord.difficulty}
                  </span>
                </div>
                <p
                  className="max-h-56 min-w-0 w-full overflow-y-auto overflow-x-hidden break-words text-sm leading-snug text-gray-600 sm:text-base"
                  style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                >
                  {selectedWord.definition}
                </p>
              </div>
            </div>
            <button
              onClick={saveWord}
              disabled={saving}
              data-testid="save-word-btn"
              className="w-full mt-4 py-3 rounded-xl font-bold text-white bg-yellow-500 hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2 active:scale-98"
            >
              <Star className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save this word! +5 🍃'}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════ CELEBRATION OVERLAY ═══════════ */}
      {showCelebration && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 lg:p-12 max-w-md w-full text-center celebration-animate">
            <div className="text-7xl mb-4 celebration-bounce">🎉</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Fredoka' }}>
              Amazing Job!
            </h2>
            <p className="text-gray-600 mb-2">You finished "{book.title}"!</p>
            <p className="text-emerald-600 font-bold mb-6 text-lg">+10 Leaf Points! 🍃</p>
            
            <div className="space-y-3">
              <button
                onClick={startQuiz}
                data-testid="start-quiz-btn"
                className="w-full py-4 rounded-2xl font-bold text-white bg-emerald-500 hover:bg-emerald-600 transition-all flex items-center justify-center gap-2 active:scale-98"
              >
                <BookOpen className="w-5 h-5" />
                Story Questions 🦉
              </button>
              <button
                onClick={onClose}
                data-testid="skip-quiz-btn"
                className="w-full py-3 rounded-2xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Maybe later 🍃
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ QUIZ OVERLAY ═══════════ */}
      {showQuiz && quizData && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 lg:p-8 max-w-lg w-full quiz-animate">
            {/* Quiz Header */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-4xl">{book.emoji}</span>
              <div className="flex-1">
                <h3 className="font-bold text-xl text-gray-800" style={{ fontFamily: 'Fredoka' }}>
                  Quiz Time!
                </h3>
                <p className="text-sm text-gray-500">{book.title}</p>
              </div>
              <button
                onClick={() => { setShowQuiz(false); onClose(); }}
                className="text-gray-400 hover:text-gray-600 text-sm"
              >
                Skip
              </button>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center gap-2 mb-8">
              {quizData.map((_, idx) => (
                <React.Fragment key={idx}>
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                      idx < quizStep ? 'bg-emerald-500 text-white' :
                      idx === quizStep ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500 ring-offset-2' :
                      'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {idx < quizStep ? '✓' : idx + 1}
                  </div>
                  {idx < quizData.length - 1 && (
                    <div className={`flex-1 h-1 rounded-full ${idx < quizStep ? 'bg-emerald-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Question */}
            <div>
              <h4 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6" style={{ fontFamily: 'Fredoka' }}>
                {quizData && quizData[quizStep] && quizData[quizStep].question}
              </h4>
              <div className="space-y-3">
                {quizData && quizData[quizStep] && quizData[quizStep].choices && quizData[quizStep].choices.map((choice, idx) => {
                  const isAnswered = quizResults.length > quizStep;
                  const isCorrect = idx === quizData[quizStep].answer;
                  const wasSelected = isAnswered && quizResults[quizStep] === (idx === quizData[quizStep].answer);
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => !isAnswered && handleQuizAnswer(idx)}
                      disabled={isAnswered}
                      data-testid={`quiz-choice-${idx}`}
                      className={`w-full p-4 rounded-2xl text-left font-medium transition-all ${
                        isAnswered && isCorrect ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-500' :
                        isAnswered && !isCorrect && wasSelected ? 'bg-red-100 text-red-700 border-2 border-red-500' :
                        isAnswered ? 'bg-gray-50 text-gray-400' :
                        'bg-gray-50 hover:bg-gray-100 text-gray-700 border-2 border-transparent hover:border-gray-200'
                      }`}
                    >
                      {choice}
                      {isAnswered && isCorrect && ' ✓'}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ STYLES ═══════════ */}
      <style>{`
        .reader-container {
          user-select: none;
        }
        
        .glass-bar {
          border-bottom: 1px solid rgba(0,0,0,0.05);
        }
        
        .nav-btn {
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .nav-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }
        
        /* Page flip animation */
        .page-wrapper.flip-mode {
          transform-style: preserve-3d;
          transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .page-wrapper.flip-mode.flipping.right {
          animation: flipRight 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .page-wrapper.flip-mode.flipping.left {
          animation: flipLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        @keyframes flipRight {
          0% { transform: rotateY(0deg) scale(${zoomLevel}); }
          50% { transform: rotateY(-15deg) scale(${zoomLevel * 0.95}); box-shadow: -20px 0 40px rgba(0,0,0,0.2); }
          100% { transform: rotateY(0deg) scale(${zoomLevel}); }
        }
        
        @keyframes flipLeft {
          0% { transform: rotateY(0deg) scale(${zoomLevel}); }
          50% { transform: rotateY(15deg) scale(${zoomLevel * 0.95}); box-shadow: 20px 0 40px rgba(0,0,0,0.2); }
          100% { transform: rotateY(0deg) scale(${zoomLevel}); }
        }
        
        /* Slide animation */
        .page-wrapper.slide-mode.flipping.right {
          animation: slideRight 0.3s ease-out;
        }
        
        .page-wrapper.slide-mode.flipping.left {
          animation: slideLeft 0.3s ease-out;
        }
        
        @keyframes slideRight {
          0% { transform: translateX(0) scale(${zoomLevel}); opacity: 1; }
          50% { transform: translateX(-30px) scale(${zoomLevel * 0.98}); opacity: 0.8; }
          100% { transform: translateX(0) scale(${zoomLevel}); opacity: 1; }
        }
        
        @keyframes slideLeft {
          0% { transform: translateX(0) scale(${zoomLevel}); opacity: 1; }
          50% { transform: translateX(30px) scale(${zoomLevel * 0.98}); opacity: 0.8; }
          100% { transform: translateX(0) scale(${zoomLevel}); opacity: 1; }
        }
        
        /* Page corner fold effect */
        .page-corner {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 0 0 40px 40px;
          border-color: transparent transparent #f3f4f6 transparent;
          transition: border-width 0.2s;
        }
        .page-wrapper:hover .page-corner {
          border-width: 0 0 50px 50px;
        }
        
        /* Page content animations */
        .page-illustration {
          animation: floatIcon 3s ease-in-out infinite;
        }
        
        @keyframes floatIcon {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        
        /* Word panel animation */
        .word-panel-animate {
          animation: slideUp 0.3s ease-out;
        }
        
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        /* Celebration animation */
        .celebration-animate {
          animation: popIn 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        
        @keyframes popIn {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        .celebration-bounce {
          animation: celebrationBounce 0.6s ease-out;
        }
        
        @keyframes celebrationBounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
        
        /* Quiz animation */
        .quiz-animate {
          animation: slideUp 0.3s ease-out;
        }
        
        /* Vocabulary word highlight */
        .vocabulary-word {
          position: relative;
        }
        .vocabulary-word::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 2px;
          background: linear-gradient(90deg, #FCD34D, #F59E0B);
          border-radius: 1px;
        }

        .reader-page-dots {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .reader-page-dots::-webkit-scrollbar {
          display: none;
        }
        
        /* Active scale effect */
        .active\\:scale-98:active {
          transform: scale(0.98);
        }
        .active\\:scale-95:active {
          transform: scale(0.95);
        }

        /* +1 Point Popup Animation */
        @keyframes float-up {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-80px) scale(1.5);
          }
        }
        
        .point-popup {
          position: fixed;
          animation: float-up 1.5s ease-out forwards;
          font-size: 32px;
          font-weight: bold;
          color: #10B981;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
          pointer-events: none;
          z-index: 10000;
        }
      `}</style>

      {/* +1 Point Popup */}
      {showPointPopup && (
        <div 
          className="point-popup"
          style={{
            left: `${pointPopupPosition.x}px`,
            top: `${pointPopupPosition.y}px`
          }}
        >
          +1 ✨
        </div>
      )}

      {/* Audio Elements */}
      <audio ref={pageTurnSound} preload="auto">
        <source src="https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3" type="audio/mpeg" />
      </audio>
      
      <audio ref={ambientMusic} loop preload="auto">
        <source src="https://assets.mixkit.co/active_storage/sfx/2491/2491-preview.mp3" type="audio/mpeg" />
      </audio>
    </div>
  );
};

export default FlipBookReader;
